/**
 * Safe Invest Regime Card — flat, minimal
 */
import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';

const R_CLR = { RISK_ON: '#10b981', RECOVERY: '#f59e0b', SIDEWAY: '#f97316', RISK_OFF: '#ef4444' };
const sClr = s => s >= 70 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';

function SafeInvestRegimeCard({ regimeData }) {
  const { t } = useTranslation();
  if (!regimeData) return null;

  const regime = regimeData.regime || 'SIDEWAY';
  const c = R_CLR[regime] || '#64748b';
  const score = regimeData.market_score || 0;
  const interp = regimeData.interpretation || {};

  const comps = [
    { label: t('safeInvest.layer1.trend', 'Xu hướng'), score: regimeData.trend_score || 0, w: 35 },
    { label: t('safeInvest.layer1.breadth', 'Độ rộng'), score: regimeData.breadth_score || 0, w: 25 },
    { label: t('safeInvest.layer1.liquidity', 'Thanh khoản'), score: regimeData.liquidity_score || 0, w: 20 },
    { label: t('safeInvest.layer1.risk', 'Rủi ro'), score: regimeData.risk_score || 0, w: 20 },
  ];

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('tradingDecision.safeInvestRegime.title', 'Safe Invest')}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, px: 0.6, py: 0.15, borderRadius: 0.5, bgcolor: alpha(c, 0.1) }}>
            {regime.replace('_', ' ')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 900, color: c, fontFamily: 'monospace', lineHeight: 1 }}>
          {score.toFixed(0)}<span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>/100</span>
        </Typography>
      </Box>

      {/* Description */}
      <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #f8fafc' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.3 }}>
          {interp.action || t('tradingDecision.safeInvestRegime.defaultAction', 'Theo dõi diễn biến thị trường cẩn thận')}
        </Typography>
      </Box>

      {/* Sub-scores as flat rows */}
      <Box sx={{ px: 1.5, py: 0.5 }}>
        {comps.map(comp => (
          <Box key={comp.label} sx={{ display: 'flex', alignItems: 'center', py: 0.35 }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', width: 70, flexShrink: 0 }}>{comp.label}</Typography>
            <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', mx: 1 }}>
              <Box sx={{ height: '100%', borderRadius: 2, bgcolor: sClr(comp.score), width: `${Math.min(comp.score, 100)}%` }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: sClr(comp.score), fontFamily: 'monospace', minWidth: 24, textAlign: 'right' }}>{comp.score.toFixed(0)}</Typography>
            <Typography sx={{ fontSize: 9, color: '#b0b8c4', ml: 0.5, minWidth: 20 }}>{comp.w}%</Typography>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
          ATR <strong style={{ color: '#475569' }}>{regimeData.atr_pct ? `${(regimeData.atr_pct * 100).toFixed(2)}%` : '–'}</strong>
          <span style={{ margin: '0 4px', color: '#e2e8f0' }}>|</span>
          DD20 <strong style={{ color: '#475569' }}>{regimeData.dd20 ? `${(regimeData.dd20 * 100).toFixed(2)}%` : '–'}</strong>
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#b0b8c4' }}>
          {regimeData.trade_date ? new Date(regimeData.trade_date).toLocaleDateString('vi-VN') : ''}
        </Typography>
      </Box>
    </Box>
  );
}

export default SafeInvestRegimeCard;
